// @flow
import { useContext, useRef, useEffect, useMemo } from 'react';
import { useMutation, MutationOptions, useQueryClient } from 'react-query';
import { handleRequestAndThrowIfError } from 'utils/api';
import {
  addHouseholdMember,
  updateHouseholdMember,
} from 'services/householdService';
import { AppContext } from 'contexts/AppContext';
import useUserProfileQuery from 'hooks/queries/useUserProfileQuery';
import useSaveShortFormMutation from 'hooks/mutations/useSaveShortFormMutation';
import APPLICANT_TYPES from 'constants/applicantTypes';
import useHouseholdMembersQuery from 'hooks/queries/useHouseholdMembersQuery';
import useSavePrimaryFormMutation from 'hooks/mutations/useSavePrimaryFormMutation';
import useSaveSecondaryFormMutation from 'hooks/mutations/useSaveSecondaryFormMutation';
import useCreateOrUpdateAffordableApplicationAdditionalDetailsMutation from 'hooks/mutations/useCreateOrUpdateAffordableApplicationAdditionalDetailsMutation';
import {
  getMinorMembers,
  isMinor as checkIsMinor,
  getIsLiveInCaretaker,
  getHeadOfHousehold,
  getIsHeadOfHousehold,
  getIsStudent,
} from 'pages/AffordaHousehold/utils';
import { getIds, updateForms } from './useCreateHouseholdMemberMutation.utils';
import QUERY_KEYS from 'constants/queryKeys';
import STUDENT_STATUS from 'constants/studentStatus';
import { v4 as uuidv4 } from 'uuid';
import HOUSEHOLD_DESIGNATION_CODES from 'constants/householdDesignationCodes';
import AFFORDABLE_RELATIONSHIPS from 'constants/affordableRelationships';
type Member = {
  type: 'leaseholders' | 'minors' | 'pets' | 'vehicles' | 'emergency-contacts',
};

const unbornChildTemplate = {
  applicantType: APPLICANT_TYPES.MINOR,
  firstName: 'Unborn Child',
  lastName: '',
  birthday: '2016-01-01',
  householdDesignation: HOUSEHOLD_DESIGNATION_CODES.UNBORN_CHILD,
  legallyResponsible: [],
  affordableRelationship: AFFORDABLE_RELATIONSHIPS.UNBORN_CHILD,
  id: '93ea78e4-080c-4d36-ae3b-c86e3e71730d',
  isFosterChild: 'no',
  relationship: AFFORDABLE_RELATIONSHIPS.CHILD,
  studentStatus: STUDENT_STATUS.NOT_A_STUDENT?.value,
  type: 'minors',
};

const findLiveInCaretaker = (householdMembers) => {
  return householdMembers?.find(
    ({ applicantType }) => applicantType === APPLICANT_TYPES.LIVE_IN_CARETAKER,
  );
};
const useCreateHouseholdMemberMutation = ({
  householdForm,
  options,
}: {
  options: MutationOptions,
} = {}) => {
  const members = useMemo(() => {
    if (!householdForm?.members?.length) return [];

    // Add unborn children to the members
    const unbornChildren = [];
    if (householdForm?.additionalMinors?.pregnancyCount) {
      const pregnancyCount = householdForm?.additionalMinors?.pregnancyCount;

      const headOfHousehold = getHeadOfHousehold(householdForm.members);

      for (let i = 0; i < pregnancyCount; i++) {
        unbornChildren.push({
          ...unbornChildTemplate,
          id: uuidv4(),
          firstName: `Unborn Child ${i + 1}`,
          lastName: headOfHousehold?.lastName ?? '',
          birthday: householdForm?.additionalMinors?.pregnancyDueDate,
          legallyResponsible: [headOfHousehold?.id ?? ''],
        });
      }
    }

    // return the new members
    return [...householdForm?.members, ...unbornChildren];
  }, [householdForm]);

  const queryClient = useQueryClient();
  const membersIdRef = useRef({});

  useEffect(() => {
    if (!members?.length) return;
    members.forEach(({ id }) => {
      membersIdRef.current[id] = membersIdRef.current[id] ?? null;
    });
  }, [members]);

  const {
    organizationId,
    propertyId,
    relationships,
    affordableRelationships,
    applicantTypes,
  } = useContext(AppContext);
  const { data: { portalUserId } = {}, refetch } = useUserProfileQuery();
  const { refetch: refetchHouseholdMembers } = useHouseholdMembersQuery();

  const saveShortFormMutation = useSaveShortFormMutation();
  const savePrimaryFormMutation = useSavePrimaryFormMutation();
  const saveSecondaryFormMutation = useSaveSecondaryFormMutation();
  const createOrUpdateAffordableApplicationAdditionalDetailsMutation =
    useCreateOrUpdateAffordableApplicationAdditionalDetailsMutation();

  const memoizedOptions = useMemo(() => {
    return {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries([
          ...QUERY_KEYS.primaryFormData({
            organizationId,
            propertyId,
          }),
          ...QUERY_KEYS.secondaryFormData({ organizationId, propertyId }),
        ]);
      },
      enabled: Boolean(portalUserId && organizationId && propertyId),
    };
  }, [options, organizationId, portalUserId, propertyId, queryClient]);

  const mutation = useMutation(
    async ({ member, studentStatus, additionalMinors }: { member: Member }) => {
      const {
        data: { applicantId, applicationId, householdId, customerId },
      } = await refetch();

      const isLiveInCaretaker = getIsLiveInCaretaker(member);
      const isMinor = checkIsMinor(member);
      const headOfHousehold = getHeadOfHousehold(members);
      const minors = getMinorMembers(members) ?? [];

      try {
        if (isLiveInCaretaker) {
          const updateLiveInCaretakerResponse =
            await handleRequestAndThrowIfError(
              updateHouseholdMember(
                organizationId,
                propertyId,
                householdId,
                member.type,
                null,
                {
                  id: applicationId,
                  adults: [
                    {
                      id: applicantId,
                      firstName: headOfHousehold.firstName,
                      lastName: headOfHousehold.lastName,
                      email: headOfHousehold.emailAddress,
                      phone: headOfHousehold.phoneNumber,
                      middleName: null,
                      preferredName: null,
                      isProspect: true,
                      deleted: false,
                      ...getIds({
                        member: headOfHousehold,
                        affordableRelationships,
                        applicantTypes,
                        relationships,
                      }),
                      affordableRelationshipId: undefined,
                      relationshipId: undefined,
                    },
                    {
                      firstName: member.firstName,
                      lastName: member.lastName,
                      phone: member.phoneNumber,
                      email: member.emailAddress,
                      deleted: false,
                      ...getIds({
                        affordableRelationships,
                        applicantTypes,
                        member,
                        relationships,
                      }),
                      affordableRelationshipId: undefined,
                      relationshipId: undefined,
                    },
                  ],
                  propertyId,
                  applicationId,
                  primaryApplicantId: applicantId,
                  householdDesignation: member.householdDesignation,
                  minors: [],
                  pets: [],
                },
              ),
            );

          const { data: refetchedHouseholdMembers } =
            await refetchHouseholdMembers();

          const liveInCaretaker = findLiveInCaretaker(
            refetchedHouseholdMembers,
          );

          await handleRequestAndThrowIfError(
            saveShortFormMutation.mutateAsync({
              applicantId: liveInCaretaker?.id,
              formData: {
                applicantInfo: {
                  firstName: member.firstName,
                  lastName: member.lastName,
                  phoneNumber: member.phoneNumber,
                  birthday: member.birthday,
                  emailAddress: member.emailAddress,
                },
                employmentInfo: {},
                studentInfo: {},
                otherInfo: {},
                residentialInfo: {},
              },
            }),
          );

          return updateLiveInCaretakerResponse;
        }

        const id = getIsHeadOfHousehold(member)
          ? customerId
          : membersIdRef.current[member.id];

        const applicantTypeKey = isMinor ? 'minor' : 'leaseholder';

        const payload = {
          [applicantTypeKey]: {
            ...member,
            ...getIds({
              affordableRelationships,
              applicantTypes,
              member,
              relationships,
            }),
            studentType: member.studentStatus,
            isStudent: getIsStudent(member),
            applicationId,
          },
        };

        // delete emailAddress because endpoint doesn't accept it
        if (id && payload?.leaseholder?.emailAddress) {
          delete payload.leaseholder.emailAddress;
        }

        if (id) {
          const updateMemberResponse = await handleRequestAndThrowIfError(
            updateHouseholdMember(
              organizationId,
              propertyId,
              householdId,
              member.type,
              id,
              payload,
            ),
          );

          if (updateMemberResponse.id) {
            await updateForms({
              additionalMinors,
              affordableRelationships,
              applicantId: updateMemberResponse.id,
              applicantTypes,
              applicationId,
              createOrUpdateAffordableApplicationAdditionalDetailsMutation,
              householdId,
              member,
              members,
              minors,
              relationships,
              savePrimaryFormMutation,
              saveSecondaryFormMutation,
              studentStatus,
            });
          }

          return updateMemberResponse;
        } else if (!isMinor) {
          const householdMemberResponse = await handleRequestAndThrowIfError(
            addHouseholdMember(
              organizationId,
              propertyId,
              householdId,
              member.type,
              null,
              payload,
            ),
          );

          membersIdRef.current[member.id] = householdMemberResponse.id;

          await updateForms({
            additionalMinors,
            affordableRelationships,
            applicantId: householdMemberResponse.id,
            applicantTypes,
            applicationId,
            createOrUpdateAffordableApplicationAdditionalDetailsMutation,
            householdId,
            member,
            members,
            minors,
            relationships,
            savePrimaryFormMutation,
            saveSecondaryFormMutation,
            studentStatus,
          });

          return householdMemberResponse;
        }
      } catch (e) {
        throw e;
      }
    },
    memoizedOptions,
  );

  return {
    ...mutation,
    isLoading:
      saveShortFormMutation.isLoading ||
      savePrimaryFormMutation.isLoading ||
      saveSecondaryFormMutation.isLoading ||
      createOrUpdateAffordableApplicationAdditionalDetailsMutation.isLoading,
  };
};

export default useCreateHouseholdMemberMutation;
